import {
    DataNavigationCalculatedEvent,
    DataNavigationState,
    MetaConfig,
    StartInfo
} from 'plugin-live-exploratory-viz/src/data-navigation/types/widget'
import { getMultiChannelIdentifier } from 'plugin-live-exploratory-viz/src/services/channel/utils'
import { delimiter } from 'plugin-live-exploratory-viz/src/services/constants'

export function realtimeSpan(span: { normalized: string }): boolean {
    const normalized = span?.normalized ?? ''
    if (normalized.includes('now') && !normalized.includes('shifted')) {
        return true
    }

    return false
}

/**
 * Merge config from multiple layers into a single `MetaConfig`
 * @param info
 * @param config
 */
export function mergeMetaConfig(
    info: StartFlowMessage<StartInfo>['events']['info'],
    config: MetaConfig,
    layer: StartFlowMessage<StartInfo>['layer']
): MetaConfig {
    if (info === undefined || !config) {
        return config
    }

    const fields = ['__color', '__description', '__precision', '__uom', '__aggregationName', '__seriesLabel']
    fields.forEach(field => {
        if (info[field]) {
            const assetId = info.__assetId
            config.assetId = assetId
            const eventField = field.substring(2)

            if (typeof info[field] === 'string') {
                const mnemonic = getMnemonicFromMeta(info)
                config = mergeConfig(assetId, mnemonic, layer, config, eventField, info[field])
            }

            config = Object.entries(info[field]).reduce(
                (config, [asset, value]) => mergeConfig(assetId, asset, layer, config, eventField, value),
                config
            )

            if (info.__lookup) {
                config.lookup = info.__assetId
            }
        }
    })

    return config
}

const mergeConfig = (
    assetId: string,
    qualifier: string,
    layer: number,
    config: MetaConfig,
    eventField: string,
    value: string | unknown
): MetaConfig => {
    const multiChannelIdentifier = getMultiChannelIdentifier(assetId, qualifier)
    const multiChannelIdentifierWithLayerPrefix = getMultiChannelIdentifierWithLayerPrefix(
        layer,
        multiChannelIdentifier
    )

    return {
        ...config,
        [multiChannelIdentifierWithLayerPrefix]: {
            ...(config[multiChannelIdentifierWithLayerPrefix] ?? {}),
            [eventField]: value
        }
    }
}

export function getYPoint(row: [number, number]): number {
    return row[1]
}

export function getMnemonicFromMeta(info: StartFlowMessage<StartInfo>['events']['info']): string {
    return Object.keys(info?.__assetConfig?.channels ?? {})?.[0] ?? ''
}

export function getCalculatedChannelNames(event: DataNavigationCalculatedEvent): string[] {
    return Object.keys(event).filter(key => key != 'timestamp')
}

export function checkSpan(timestamp: number, span: [number, number]): boolean {
    return timestamp >= span[0] && timestamp <= span[1]
}

export function sortCompare(a: [number, number], b: [number, number]): number {
    return a[0] - b[0]
}

export function getMultiChannelIdentifierWithLayerPrefix(
    layer: StartFlowMessage<StartInfo>['layer'],
    multiChannelIdentifier: string
): string {
    return `${layer}${delimiter}${multiChannelIdentifier}`
}

export function getMultiChannelIdentifierFromMultiChannelIdentifierWithLayerPrefix(
    multiChannelIdentifierWithLayerPrefix: string
): string {
    return multiChannelIdentifierWithLayerPrefix?.split(delimiter)?.slice(1)?.join(delimiter) ?? ''
}

export function filterChannelsByLayer(dataNavigationSeries: DataNavigationState['series'], layer: string): string[] {
    return Object.keys(dataNavigationSeries ?? {}).filter(channel => channel.startsWith(layer))
}

export function getMultiChannelIdentifierWithLayerPrefixFromLayerByMnemonic(
    multiChannelsIdentifierWithLayerPrefix: string[],
    mnemonic: string
): string {
    return multiChannelsIdentifierWithLayerPrefix.find(
        multiChannelIdentifierWithLayerPrefix =>
            multiChannelIdentifierWithLayerPrefix.split(delimiter)?.[2] === mnemonic
    )
}
